import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import { apiService } from '../../services/apiService';
import { EQUIPMENT_TYPES, EXTINGUISHER_TYPES } from '../../utils/constants';

const EditEquipment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const returnTo =
    new URLSearchParams(location.search).get('returnTo') || '/equipment';
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    type: '',
    clientId: '',
    siteId: '',
    serialNumber: '',
    extinguisherType: '',
    capacity: '',
    manufacturingDate: '',
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    expirationDate: '',
    location: '',
    vehicleRegistration: '',
    vehicleSite: '',
    physicalCondition: '',
    spareParts: '',
    firstAidKit: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (formData.clientId) {
      loadSites(formData.clientId);
    } else {
      setSites([]);
    }
  }, [formData.clientId]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      const [equipmentData, clientsData] = await Promise.all([
        apiService.getEquipmentById(id),
        apiService.getClients(),
      ]);

      setClients(clientsData);
      
      // Format dates for input fields
      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0];
      };

      setFormData({
        type: equipmentData.type || '',
        clientId: equipmentData.clientId || '',
        siteId: equipmentData.siteId || '',
        serialNumber: equipmentData.serialNumber || '',
        extinguisherType: equipmentData.extinguisherType || '',
        capacity: equipmentData.capacity || '',
        manufacturingDate: formatDate(equipmentData.manufacturingDate),
        lastMaintenanceDate: formatDate(equipmentData.lastMaintenanceDate),
        nextMaintenanceDate: formatDate(equipmentData.nextMaintenanceDate),
        expirationDate: formatDate(equipmentData.expirationDate),
        location: equipmentData.location || '',
        vehicleRegistration: equipmentData.vehicleRegistration || '',
        vehicleSite: equipmentData.vehicleSite || '',
        physicalCondition: equipmentData.physicalCondition || '',
        spareParts: equipmentData.spareParts || '',
        firstAidKit: equipmentData.firstAidKit || '',
        notes: equipmentData.notes || '',
      });
    } catch (error) {
      console.error('Failed to load equipment:', error);
      setErrors({ load: 'Impossible de charger les données de l\'équipement' });
    } finally {
      setLoadingData(false);
    }
  };

  const loadSites = async (clientId) => {
    try {
      const data = await apiService.getSitesByClient(clientId);
      setSites(data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-calculate next maintenance date when lastMaintenanceDate changes
    if (name === 'lastMaintenanceDate' && value) {
      const lastDate = new Date(value);
      lastDate.setMonth(lastDate.getMonth() + 6); // Add 6 months
      const nextDate = lastDate.toISOString().split('T')[0];
      setFormData({ ...formData, [name]: value, nextMaintenanceDate: nextDate });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.clientId) newErrors.clientId = 'Le client est requis';
    if (!formData.siteId) newErrors.siteId = 'Le site est requis';
    if (!formData.serialNumber) newErrors.serialNumber = 'Le numéro de série est requis';

    // Validate based on equipment type
    if (formData.type === EQUIPMENT_TYPES.EXTINGUISHER) {
      if (!formData.extinguisherType) newErrors.extinguisherType = "Le type d'extincteur est requis";
      if (!formData.capacity) newErrors.capacity = 'La capacité est requise';
    }

    if (!formData.lastMaintenanceDate) newErrors.lastMaintenanceDate = 'La date de vérification/recharge est requise';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const result = await apiService.updateEquipment(id, formData);
      if (result.success) {
        navigate('/equipment');
      } else {
        navigate(returnTo);
      }
    } catch (error) {
      console.error('Failed to update equipment:', error);
      setErrors({ submit: error.message || "Erreur lors de la mise à jour de l'équipement" });
    } finally {
      setLoading(false);
    }
  };

  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: `${client.name} - ${client.company}`,
  }));

  const siteOptions = sites.map((site) => ({
    value: site.id,
    label: site.name,
  }));

  const extinguisherTypeOptions = Object.values(EXTINGUISHER_TYPES).map((type) => ({
    value: type,
    label: type,
  }));

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (errors.load) {
    return (
      <div className="space-y-6">
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate(returnTo)}
          className="mb-4"
        >
          Retour
        </Button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{errors.load}</p>
        </div>
      </div>
    );
  }

  const isExtinguisher = formData.type === EQUIPMENT_TYPES.EXTINGUISHER;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate(returnTo)}
          className="mb-4"
        >
          Retour
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          Modifier {isExtinguisher ? "l'Extincteur" : "l'Équipement"}
        </h1>
        <p className="text-gray-600 mt-1">Mettez à jour les informations de l'équipement</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Informations générales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Client"
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              options={clientOptions}
              placeholder="Sélectionner un client"
              error={errors.clientId}
              required
            />
            <Select
              label="Site"
              name="siteId"
              value={formData.siteId}
              onChange={handleChange}
              options={siteOptions}
              placeholder={formData.clientId ? "Sélectionner un site" : "Choisir d'abord un client"}
              error={errors.siteId}
              disabled={!formData.clientId}
              required
            />
            <Input
              label="Numéro de série"
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
              error={errors.serialNumber}
              required
            />
            {isExtinguisher && (
              <>
                <Select
                  label="Type d'extincteur"
                  name="extinguisherType"
                  value={formData.extinguisherType}
                  onChange={handleChange}
                  options={extinguisherTypeOptions}
                  placeholder="Sélectionner le type"
                  error={errors.extinguisherType}
                  required
                />
                <Input
                  label="Capacité (kg ou L)"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  error={errors.capacity}
                  placeholder="Ex: 6 kg"
                  required
                />
              </>
            )}
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Dates importantes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Date de vérification / Recharge"
              type="date"
              name="lastMaintenanceDate"
              value={formData.lastMaintenanceDate}
              onChange={handleChange}
              error={errors.lastMaintenanceDate}
              helpText="La prochaine date sera calculée automatiquement (+6 mois)"
              required
            />
            <Input
              label="Prochaine vérification"
              type="date"
              name="nextMaintenanceDate"
              value={formData.nextMaintenanceDate}
              onChange={handleChange}
              helpText="Calculée automatiquement (+6 mois)"
              disabled
            />
            <Input
              label="Date de recharge"
              type="date"
              name="expirationDate"
              value={formData.expirationDate}
              onChange={handleChange}
            />
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Emplacement et État Physique
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 border border-gray-200 w-1/4">
                    Champ
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 border border-gray-200">
                    Valeur
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 border border-gray-200 bg-gray-50">
                    <span className="font-medium text-gray-700">Emplacement</span>
                    <p className="text-xs text-gray-500 mt-1">Localisation de l'équipement</p>
                  </td>
                  <td className="px-4 py-2 border border-gray-200">
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Ex: Entrée principale, Bureau 101, Couloir 2ème étage..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm"
                    />
                  </td>
                </tr>
                {isExtinguisher && (
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 border border-gray-200 bg-gray-50">
                      <span className="font-medium text-gray-700">Aspect Physique</span>
                      <p className="text-xs text-gray-500 mt-1">État général de l'extincteur</p>
                    </td>
                    <td className="px-4 py-2 border border-gray-200">
                      <textarea
                        name="physicalCondition"
                        value={formData.physicalCondition}
                        onChange={handleChange}
                        rows={2}
                        placeholder="Ex: Bon état, quelques éraflures, peinture écaillée, manomètre en bon état..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm resize-none"
                      />
                    </td>
                  </tr>
                )}
                {isExtinguisher && (
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 border border-gray-200 bg-gray-50">
                      <span className="font-medium text-gray-700">Pièces détachées</span>
                      <p className="text-xs text-gray-500 mt-1">Pièces remplacées ou vérifiées</p>
                    </td>
                    <td className="px-4 py-2 border border-gray-200">
                      <textarea
                        name="spareParts"
                        value={formData.spareParts}
                        onChange={handleChange}
                        rows={2}
                        placeholder="Ex: Flexible neuf, Manomètre remplacé, Goupille changée, Tube plongeur vérifié..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm resize-none"
                      />
                    </td>
                  </tr>
                )}
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 border border-gray-200 bg-gray-50">
                    <span className="font-medium text-gray-700">Boîte à pharmacie</span>
                    <p className="text-xs text-gray-500 mt-1">État et contenu de la boîte à pharmacie</p>
                  </td>
                  <td className="px-4 py-2 border border-gray-200">
                    <textarea
                      name="firstAidKit"
                      value={formData.firstAidKit}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Ex: Complet, Bandages vérifiés, Désinfectant à remplacer, Date d'expiration OK..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm resize-none"
                    />
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 border border-gray-200 bg-gray-50">
                    <span className="font-medium text-gray-700">Notes</span>
                    <p className="text-xs text-gray-500 mt-1">Informations complémentaires</p>
                  </td>
                  <td className="px-4 py-2 border border-gray-200">
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Observations, remarques particulières..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-sm resize-none"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/equipment')}
          >
            onClick={() => navigate(returnTo)}
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Enregistrer les modifications
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditEquipment;


